import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useNavigationType } from "react-router-dom";
import { ThemeProvider, Global, css } from "@emotion/react";
import emotionReset from "emotion-reset";
import { isEmpty } from "lodash";
import { useLocation } from "react-router";
import "vanilla-cookieconsent/dist/cookieconsent.css";

import ErrorBoundary from "core/Components/Errors/ErrorBoundary";
import ToastMessage from "core/Components/Utils/ToastMessage";
import AppContext from "core/contexts/AppContext";
import useSetState from "core/hooks/useSetState";
import { StripePlan } from "core/hooks/useStripePlans";
import fonts, { fontImports } from "theme/fonts";
import theme from "theme/index";
import typography from "theme/typography";
import AppPage from "./AppPage";
import { FLAG_COOKIE_SETTINGS } from "core/includes/featureFlags";
import { INTERNAL_FEATURE_KEY, setLocalStorageFeatureFlags } from "core/includes/site";
import UserSession from "core/Components/Utils/UserSession";
import { CookieContextProvider } from "core/contexts/CookieContext";
import window, {hasWindow} from 'core/includes/window';
import {NoSsr} from "core/Components/Ssr";

type DEFAULT_STATE = {
    loading: boolean,
    hasUserBeenLoaded: boolean,
    isSkipLoadingPage: boolean,
    globalCurrencyOptions: GlobalCurrencyOptions,
    countryCode: {
        defaultCode: string,
        code: string,
    },
    isCountryDropdownChanged: boolean,
    currentURLSegment?: string,
    currentPageName?: string,
    plans: StripePlan[] | null,
    featureFlags: string[],
    cart: Cart | null,
    isOpenCartPopup: boolean,
    // by IP
    ipCountry: CountryCodeItem,
    pageClassNames: PageClassName[],
    isCartLoading: boolean,
    countries: CountryItem[],
    user: User | null
};

const DEFAULT_APP_STATE = {
    loading: hasWindow,
    hasUserBeenLoaded: false,
    isSkipLoadingPage: hasWindow,
    globalCurrencyOptions: {
        isGlobal: false,
        currency: "USD",
        currencySymbol: "$",
    },
    countryCode: {
        defaultCode: "",
        code: "",
        name: "",
        currency: "",
        taxLabel: "",
        taxRate: "",
    },
    isCountryDropdownChanged: false,
    currentURLSegment: "",
    currentPageName: "",
    plans: null,
    featureFlags: [],
    isLoadingFeatureFlags: true,
    isOpenCartPopup: false,
    cart: null,
    ipCountry: {
        id: "",
        code: "",
        name: "",
        currency: "",
        taxLabel: "",
        taxRate: "",
    },
    pageClassNames: [],
    isCartLoading: false,
    countries: [],
    user: null,
};

function App() {
    const appState: [any, any] = useSetState<DEFAULT_STATE>(DEFAULT_APP_STATE);
    const [footerMenu, setFooterMenu] = useState<{ menu: [any], side: [any], bottom: [any], all: [StandardPage] }>();
    const [showBreadcrumb, setShowBreadcrumb] = useState(true);
    const location = useLocation();
    const currentPathname = useRef("");

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get(INTERNAL_FEATURE_KEY)) {
            try {
                const featureFlags = JSON.parse(params.get(INTERNAL_FEATURE_KEY) || "[]")
                setLocalStorageFeatureFlags(featureFlags)
            } catch { /* empty */ }
        }
    }, [location.search]);

    useEffect(() => {
        const _hsq = (window as any)._hsq = (window as any)._hsq || [];
        _hsq.push(['setPath', window.location.href]);
        _hsq.push(['trackPageView']);
    }, [location]);

    // All menu information of the Footer
    const footerMenuHandler = useCallback((pages: any) => {
        if (!isEmpty(pages)) {
            setFooterMenu(pages);
        }
    }, []);

    const breadcrumbHandler = useCallback((showBreadcrumb: boolean) => {
        setShowBreadcrumb(showBreadcrumb);
    }, [setShowBreadcrumb]);

    const ScrollToTop = () => {
        const { pathname } = useLocation();
        const navigationType = useNavigationType(); // PUSH, POP, REPLACE

        useEffect(() => {
            if (navigationType === 'PUSH' && pathname !== currentPathname.current) {
                window.scrollTo({ top: 0, left: 0 });
            }
            currentPathname.current = pathname;
        }, [navigationType, pathname]);

        return null;
    };

    const globalStyles = css`
        ${emotionReset}
        ${fontImports}
        ${typography}
        html,
        body,
        #root {
            width: 100%;
            height: 100%;

            font-size: ${fonts.baseSize};
            line-height: ${fonts.lineHeight};
            font-family: ${fonts.primary};
            font-weight: ${fonts.weights.base};

            .radius-box {
                -webkit-mask-image: -webkit-radial-gradient(white 0%, black 0%);
            }
        }

        * {
            box-sizing: border-box;
        }
    `;

    return (
        <AppContext.Provider value={appState}>
            <CookieContextProvider isConsentingEnabled={appState[0].featureFlags?.includes(FLAG_COOKIE_SETTINGS)}>
                <ThemeProvider theme={theme}>
                    <Global styles={globalStyles} />
                    <ScrollToTop />
                    <ErrorBoundary>
                        <ToastMessage />
                        <Suspense fallback={null}>
                            <NoSsr>
                                <UserSession />
                            </NoSsr>
                            <AppPage
                                showBreadcrumb={showBreadcrumb}
                                breadcrumbHandler={breadcrumbHandler}
                                footerMenu={footerMenu}
                                footerMenuHandler={footerMenuHandler}
                            />
                        </Suspense>
                    </ErrorBoundary>
                </ThemeProvider>
            </CookieContextProvider>
        </AppContext.Provider>
    );
}

export default App;
